import { z } from 'zod';
import { directionSchema } from './playerSchema.js';
const MAP_MIN_SIZE = 4;
const MAP_MAX_SIZE = 320;
const tileLayerSchema = z.array(z.array(z.string().max(64)));
/** Bodenhoehen in Metern; grosszuegig begrenzt, damit Unsinn auffaellt. */
const HOEHE_MIN_M = -50;
const HOEHE_MAX_M = 500;
const hoehenSchema = z.array(z.array(z.number().finite().min(HOEHE_MIN_M).max(HOEHE_MAX_M)));
const zoneRectSchema = z.object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    w: z.number().int().positive(),
    h: z.number().int().positive(),
});
const zoneSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(64),
    kind: z.enum(['open', 'meeting', 'lounge', 'quiet']),
    rects: z.array(zoneRectSchema).min(1),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    audio: z.enum(['proximity', 'zone']),
    team: z.string().max(64).optional(),
    sign: z.object({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative() }).optional(),
    restricted: z.boolean().optional(),
});
const mapObjectSchema = z.object({
    id: z.string().min(1).max(64),
    type: z.enum([
        'whiteboard', 'stickyboard', 'youtube', 'link', 'screen', 'picture', 'calendar',
        'fridge', 'coffee', 'arcade', 'boardgame',
    ]),
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    w: z.number().int().positive(),
    h: z.number().int().positive(),
    facing: directionSchema,
    interactRadius: z.number().min(0.5).max(10),
    props: z.object({
        videoId: z.string().max(32).optional(),
        url: z.string().url().max(2048).optional(),
        label: z.string().max(64).optional(),
        game: z.enum(['chess', 'connect4', 'ttt']).optional(),
    }),
});
const spawnSchema = z.object({
    id: z.string().min(1).max(64),
    x: z.number().nonnegative(),
    y: z.number().nonnegative(),
    dir: directionSchema,
});
/**
 * Validiert ein komplettes Map-Dokument inkl. Konsistenz der Dimensionen
 * (alle Layer und das Kollisionsraster muessen width x height entsprechen).
 */
export const mapDataSchema = z
    .object({
    version: z.number().int().positive(),
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(64),
    tileSize: z.number().int().min(8).max(128),
    width: z.number().int().min(MAP_MIN_SIZE).max(MAP_MAX_SIZE),
    height: z.number().int().min(MAP_MIN_SIZE).max(MAP_MAX_SIZE),
    theme: z.string().min(1).max(32),
    layers: z.object({
        floor: tileLayerSchema,
        walls: tileLayerSchema,
        decor: tileLayerSchema,
    }),
    collision: z.array(z.string().regex(/^[01]*$/)),
    zones: z.array(zoneSchema),
    objects: z.array(mapObjectSchema),
    spawns: z.array(spawnSchema).min(1),
    ansichten: z.enum(['2d', '3d', 'beide']).default('beide'),
    hoehen: hoehenSchema.optional(),
})
    .superRefine((map, ctx) => {
    const layers = [
        ['floor', map.layers.floor],
        ['walls', map.layers.walls],
        ['decor', map.layers.decor],
    ];
    for (const [name, layer] of layers) {
        if (layer.length !== map.height || layer.some((row) => row.length !== map.width)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Layer "${name}" passt nicht zu ${map.width}x${map.height}`,
            });
        }
    }
    if (map.collision.length !== map.height || map.collision.some((row) => row.length !== map.width)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Kollisionsraster passt nicht zu ${map.width}x${map.height}`,
        });
    }
    if (map.hoehen && (map.hoehen.length !== map.height || map.hoehen.some((row) => row.length !== map.width))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Höhenraster passt nicht zu ${map.width}x${map.height}`,
        });
    }
    for (const spawn of map.spawns) {
        if (spawn.x >= map.width || spawn.y >= map.height) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Spawn "${spawn.id}" liegt ausserhalb der Map` });
        }
    }
});
//# sourceMappingURL=mapSchema.js.map